// ============================================================
// LinuxCrontab.jsx
//
// Branded, responsive course content page for the
// "Linux Crontab & Task Scheduling" lesson.
//
// Brand palette (consistent across all lesson pages):
//   Background  → #100720  (deep purple-black)
//   Primary     → goldenrod / #DAA520
//   Accent      → #FFD700  (bright gold highlights)
//   Text        → #FFFFFF  (white)
//   Surface     → rgba(255,255,255,0.04) with gold borders
//
// Built with:
//   - Material UI (MUI v5) for layout, typography & icons
//   - Fully responsive (xs → xl breakpoints)
//   - Heavily commented for junior developers
// ============================================================

import React, { useState } from "react";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Collapse,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import AccessTimeIcon          from "@mui/icons-material/AccessTime";
import ScheduleIcon            from "@mui/icons-material/Schedule";
import TerminalIcon            from "@mui/icons-material/Terminal";
import CodeIcon                from "@mui/icons-material/Code";
import FolderOpenIcon          from "@mui/icons-material/FolderOpen";
import SettingsIcon            from "@mui/icons-material/Settings";
import BuildIcon               from "@mui/icons-material/Build";
import LayersIcon              from "@mui/icons-material/Layers";
import LinkIcon                from "@mui/icons-material/Link";
import ExpandMoreIcon          from "@mui/icons-material/ExpandMore";
import ExpandLessIcon          from "@mui/icons-material/ExpandLess";
import ContentCopyIcon         from "@mui/icons-material/ContentCopy";
import DoneIcon                from "@mui/icons-material/Done";
import CheckCircleOutlineIcon  from "@mui/icons-material/CheckCircleOutline";
import CheckCircleIcon         from "@mui/icons-material/CheckCircle";
import LightbulbIcon           from "@mui/icons-material/Lightbulb";
import StarIcon                from "@mui/icons-material/Star";
import SchoolIcon              from "@mui/icons-material/School";
import WarningAmberIcon        from "@mui/icons-material/WarningAmber";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import StorageIcon             from "@mui/icons-material/Storage";
import ArticleIcon             from "@mui/icons-material/Article";
import AutorenewIcon           from "@mui/icons-material/Autorenew";
import EmailIcon               from "@mui/icons-material/Email";
import BugReportIcon           from "@mui/icons-material/BugReport";
import PersonIcon              from "@mui/icons-material/Person";
import AdminPanelSettingsIcon  from "@mui/icons-material/AdminPanelSettings";

// ── Brand tokens ──────────────────────────────────────────
// All colours in one object — change here to retheme everything.
const BRAND = {
    bg:            "#233D4D",
    surface:       "rgba(255,255,255,0.04)",
    border:        "rgba(218,165,32,0.25)",
    borderHover:   "rgba(218,165,32,0.6)",
    gold:          "#DAA520",
    goldBright:    "#FFD700",
    goldGlow:      "rgba(218,165,32,0.15)",
    white:         "#FFFFFF",
    muted:         "rgba(255,255,255,0.55)",
    codeBg:        "rgba(0,0,0,0.45)",
    codeText:      "#FFD700",
    danger:        "rgba(255,80,80,0.12)",
    dangerBorder:  "rgba(255,80,80,0.35)",
    dangerText:    "#ff8080",
    success:       "rgba(39,201,63,0.10)",
    successBorder: "rgba(39,201,63,0.35)",
    successText:   "#27c93f",
    // Cron field highlight colours — used in the syntax diagram
    fieldMin:    "#f472b6",   // pink   – minute
    fieldHour:   "#fb923c",   // orange – hour
    fieldDom:    "#facc15",   // yellow – day of month
    fieldMon:    "#4ade80",   // green  – month
    fieldDow:    "#60a5fa",   // blue   – day of week
    fieldCmd:    "#DAA520",   // gold   – command
};

// ── Shared heading style ──────────────────────────────────
const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Shared MUI TableCell style helper ────────────────────
const tcStyle = (extra = {}) => ({
    borderColor: BRAND.border,
    color: BRAND.muted,
    fontSize: 13,
    py: 1.4,
    ...extra,
});

// ============================================================
//  SUB-COMPONENT: SectionTitle
//  Gold icon circle + heading — reused by every section.
//  Props:
//    icon  – MUI icon element
//    label – heading text string
// ============================================================
function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box
                sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    background: BRAND.goldGlow,
                    border: `1px solid ${BRAND.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                }}
            >
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ConceptCard
//  Hoverable card for one cron concept.
//  Props:
//    icon        – MUI icon element
//    title       – short concept name
//    description – plain-English explanation
// ============================================================
function ConceptCard({ icon, title, description }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                height: "100%",
                background: BRAND.surface,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 3,
                transition: "border-color 0.25s, box-shadow 0.25s",
                "&:hover": {
                    borderColor: BRAND.borderHover,
                    boxShadow: `0 0 20px ${BRAND.goldGlow}`,
                },
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.2 }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 22 } })}
                <Typography
                    sx={{ fontFamily: "'Georgia', serif", fontWeight: 700, color: BRAND.white, fontSize: 14 }}
                >
                    {title}
                </Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.65 }}>
                {description}
            </Typography>
        </Paper>
    );
}

// ============================================================
//  SUB-COMPONENT: CodeBlock
//  Terminal-style block with copy-to-clipboard.
//  Props:
//    lines – array of { cmd: string, comment?: string }
// ============================================================
function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box
            sx={{
                background: BRAND.codeBg,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 2,
                overflow: "hidden",
                mb: 2,
            }}
        >
            {/* ── Terminal title bar ── */}
            <Box
                sx={{
                    px: 2,
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: `1px solid ${BRAND.border}`,
                    background: "rgba(0,0,0,0.3)",
                }}
            >
                {/* Decorative macOS-style dots */}
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>

                {/* Copy button with tick confirmation */}
                <Tooltip title={copied ? "Copied!" : "Copy"} placement="top">
                    <Box
                        onClick={handleCopy}
                        sx={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            color: copied ? BRAND.goldBright : BRAND.muted,
                            fontSize: 12,
                            transition: "color 0.2s",
                            "&:hover": { color: BRAND.gold },
                        }}
                    >
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>

            {/* ── Command lines ── */}
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {/* Optional grey comment line above the command */}
                        {comment && (
                            <Typography
                                sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.3 }}
                            >
                                # {comment}
                            </Typography>
                        )}
                        <Typography
                            sx={{
                                fontFamily: "monospace",
                                fontSize: { xs: 12, md: 13 },
                                color: BRAND.codeText,
                                wordBreak: "break-all",
                            }}
                        >
                            {cmd}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: WarningBox
//  Red-tinted panel for important warnings.
//  Props: children – JSX content
// ============================================================
function WarningBox({ children }) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                p: 2,
                mb: 2,
                background: BRAND.danger,
                border: `1px solid ${BRAND.dangerBorder}`,
                borderRadius: 2,
            }}
        >
            <WarningAmberIcon sx={{ color: BRAND.dangerText, flexShrink: 0, mt: 0.1 }} />
            <Typography sx={{ color: BRAND.dangerText, fontSize: 13, lineHeight: 1.7 }}>
                {children}
            </Typography>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: SuccessBox
//  Green-tinted best-practice tip box.
//  Props: children – JSX content
// ============================================================
function SuccessBox({ children }) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                p: 2,
                mb: 2,
                background: BRAND.success,
                border: `1px solid ${BRAND.successBorder}`,
                borderRadius: 2,
            }}
        >
            <CheckCircleIcon sx={{ color: BRAND.successText, flexShrink: 0, mt: 0.1 }} />
            <Typography sx={{ color: BRAND.successText, fontSize: 13, lineHeight: 1.7 }}>
                {children}
            </Typography>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ExpandableExample
//  Accordion for optional deeper content.
//  Props:
//    title    – accordion header label
//    icon     – MUI icon element
//    children – JSX body content
// ============================================================
function ExpandableExample({ title, icon, children }) {
    const [open, setOpen] = useState(false);

    return (
        <Box
            sx={{
                border: `1px solid ${open ? BRAND.borderHover : BRAND.border}`,
                borderRadius: 2.5,
                overflow: "hidden",
                mb: 1.5,
                transition: "border-color 0.25s",
            }}
        >
            {/* Clickable header */}
            <Box
                onClick={() => setOpen((p) => !p)}
                sx={{
                    px: 2.5,
                    py: 1.8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    background: open ? BRAND.goldGlow : BRAND.surface,
                    transition: "background 0.25s",
                    "&:hover": { background: BRAND.goldGlow },
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
                    <Typography sx={{ color: BRAND.white, fontWeight: 600, fontSize: 14 }}>
                        {title}
                    </Typography>
                </Box>
                {open
                    ? <ExpandLessIcon sx={{ color: BRAND.gold }} />
                    : <ExpandMoreIcon sx={{ color: BRAND.muted }} />
                }
            </Box>

            {/* Smooth MUI Collapse animation */}
            <Collapse in={open}>
                <Box
                    sx={{
                        px: 2.5,
                        py: 2,
                        background: "rgba(0,0,0,0.25)",
                        borderTop: `1px solid ${BRAND.border}`,
                    }}
                >
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ResourceLink
//  Styled external-link row.
//  Props:
//    href  – URL
//    label – display name
//    sub   – short description
// ============================================================
function ResourceLink({ href, label, sub }) {
    return (
        <Box
            component="a"
            href={href}
            target="_blank"
            rel="noreferrer"
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2,
                mb: 1.2,
                textDecoration: "none",
                border: `1px solid ${BRAND.border}`,
                borderRadius: 2,
                background: BRAND.surface,
                transition: "border-color 0.25s, box-shadow 0.25s",
                "&:hover": {
                    borderColor: BRAND.borderHover,
                    boxShadow: `0 0 16px ${BRAND.goldGlow}`,
                },
            }}
        >
            <LinkIcon sx={{ color: BRAND.gold, fontSize: 20, flexShrink: 0 }} />
            <Box>
                <Typography sx={{ color: BRAND.gold, fontWeight: 700, fontSize: 14, lineHeight: 1.3 }}>
                    {label}
                </Typography>
                {sub && (
                    <Typography sx={{ color: BRAND.muted, fontSize: 12, mt: 0.3 }}>{sub}</Typography>
                )}
            </Box>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: CronSyntaxDiagram
//  The visual colour-coded cron syntax diagram.
//  Each field is highlighted with its own colour so students
//  can instantly map position → meaning.
// ============================================================
function CronSyntaxDiagram() {
    // Each field: position label, value range, and a unique colour
    const fields = [
        { label: "Minute",       range: "0–59",  color: BRAND.fieldMin  },
        { label: "Hour",         range: "0–23",  color: BRAND.fieldHour },
        { label: "Day of Month", range: "1–31",  color: BRAND.fieldDom  },
        { label: "Month",        range: "1–12",  color: BRAND.fieldMon  },
        { label: "Day of Week",  range: "0–6",   color: BRAND.fieldDow  },
        { label: "Command",      range: "",      color: BRAND.fieldCmd  },
    ];

    return (
        <Box
            sx={{
                background: BRAND.codeBg,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 2,
                p: { xs: 2, md: 3 },
                mb: 3,
                overflowX: "auto",        // scroll horizontally on small screens
            }}
        >
            {/* ── Colour-coded expression row ── */}
            {/* Each asterisk is coloured to match its field legend below */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 1.5, md: 2.5 },
                    mb: 2.5,
                    flexWrap: "nowrap",     // keep on one line for alignment
                }}
            >
                {fields.map(({ label, color }, i) => (
                    <Typography
                        key={label}
                        sx={{
                            fontFamily: "monospace",
                            fontSize: { xs: 20, md: 28 },
                            fontWeight: 800,
                            color,
                            // The command field shows a placeholder string
                            minWidth: i === 5 ? { xs: 80, md: 140 } : "auto",
                            lineHeight: 1,
                        }}
                    >
                        {i === 5 ? "/path/cmd" : "*"}
                    </Typography>
                ))}
            </Box>

            {/* ── Connector lines: vertical arrows pointing to labels ── */}
            {/*
        We use a simple flex row of colour-coded boxes to act as
        "column headers" aligned below each asterisk.
        On mobile they wrap — still readable, just stacked.
      */}
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                }}
            >
                {fields.map(({ label, range, color }) => (
                    <Box
                        key={label}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.8,
                            background: `${color}15`,
                            border: `1px solid ${color}45`,
                            borderRadius: 20,
                            px: 1.2,
                            py: 0.4,
                        }}
                    >
                        {/* Colour dot */}
                        <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: color, flexShrink: 0 }} />
                        <Typography sx={{ fontFamily: "monospace", fontSize: 11, color, lineHeight: 1.3 }}>
                            {label}{range ? ` (${range})` : ""}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: CronExampleRow
//  One row in the cron examples reference table.
//  Props:
//    expression  – the cron expression string
//    description – plain-English meaning
//    category    – label shown as a chip (e.g. "Daily")
//    color       – chip accent colour
// ============================================================
function CronExampleRow({ expression, description, category, color }) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: { xs: 1, sm: 1.5 },
                py: 1.4,
                px: 1.5,
                borderBottom: `1px solid ${BRAND.border}`,
                "&:last-child": { borderBottom: "none" },
                transition: "background 0.2s",
                "&:hover": { background: "rgba(255,255,255,0.02)" },
            }}
        >
            {/* Category chip */}
            <Chip
                label={category}
                size="small"
                sx={{
                    background: `${color}18`,
                    border: `1px solid ${color}55`,
                    color,
                    fontFamily: "monospace",
                    fontSize: 10,
                    height: 20,
                    letterSpacing: 0.5,
                    flexShrink: 0,
                    alignSelf: "center",
                }}
            />

            {/* Cron expression in gold monospace */}
            <Typography
                sx={{
                    fontFamily: "monospace",
                    fontSize: { xs: 12, md: 13 },
                    color: BRAND.codeText,
                    fontWeight: 600,
                    minWidth: { xs: "100%", sm: 180 },
                    flexShrink: 0,
                    wordBreak: "break-all",
                }}
            >
                {expression}
            </Typography>

            {/* Human-readable description */}
            <Typography sx={{ fontSize: 13, color: BRAND.muted, flex: 1, lineHeight: 1.6 }}>
                {description}
            </Typography>
        </Box>
    );
}

// ============================================================
//  MAIN COMPONENT: LinuxCrontab
//  Full "Linux Crontab & Task Scheduling" lesson page.
// ============================================================
export default function LinuxCrontab() {
    return (
        // ── Page wrapper ─────────────────────────────────────
        <Box
            sx={{
                minHeight: "100vh",
                background: BRAND.bg,
                // Subtle diagonal texture — consistent across all lesson pages
                backgroundImage: `repeating-linear-gradient(
          -45deg,
          rgba(218,165,32,0.02) 0px,
          rgba(218,165,32,0.02) 1px,
          transparent 1px,
          transparent 12px
        )`,
                py: { xs: 4, md: 7 },
                px: { xs: 2, sm: 3 },
            }}
        >
            <Container maxWidth="lg">

                {/* ══════════════════════════════════════════════
            HERO HEADER
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>

                    {/* Module badge */}
                    <Chip
                        icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />}
                        label="Automation · Module 06"
                        sx={{
                            mb: 2.5,
                            background: BRAND.goldGlow,
                            border: `1px solid ${BRAND.border}`,
                            color: BRAND.gold,
                            fontFamily: "monospace",
                            fontSize: 11,
                            letterSpacing: 1,
                            textTransform: "uppercase",
                        }}
                    />

                    {/* Title with gold gradient on key word */}
                    <Typography
                        variant="h3"
                        sx={{
                            ...sectionHeading,
                            fontSize: { xs: 26, sm: 34, md: 42 },
                            mb: 2,
                            "& span": {
                                background: `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldBright})`,
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                            },
                        }}
                    >
                        Linux <span>Crontab</span> &amp; Task Scheduling
                    </Typography>

                    {/* Intro */}
                    <Typography
                        sx={{
                            color: BRAND.muted,
                            fontSize: { xs: 14, md: 16 },
                            maxWidth: 680,
                            lineHeight: 1.8,
                            mx: { xs: "auto", md: 0 },
                            mb: 3,
                        }}
                    >
                        <strong style={{ color: BRAND.white }}>cron</strong> is the Unix task scheduler —
                        a daemon that wakes up every minute, checks for scheduled jobs, and runs them
                        automatically. Whether you need a daily backup, hourly health check, or a script
                        that fires every 5 minutes, cron handles it with a single line of configuration.
                        Mastering cron is essential for any Linux sysadmin or developer who wants to
                        automate repetitive work.
                    </Typography>

                    {/* Quick stats */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, justifyContent: { xs: "center", md: "flex-start" } }}>
                        {[
                            { label: "~2 hours",  sub: "read time"    },
                            { label: "Beginner",  sub: "level"         },
                            { label: "5 fields",  sub: "syntax"        },
                        ].map(({ label, sub }) => (
                            <Box
                                key={label}
                                sx={{
                                    textAlign: "center",
                                    background: BRAND.surface,
                                    border: `1px solid ${BRAND.border}`,
                                    borderRadius: 2,
                                    px: 2.5,
                                    py: 1.2,
                                }}
                            >
                                <Typography sx={{ color: BRAND.goldBright, fontWeight: 800, fontSize: 15 }}>{label}</Typography>
                                <Typography sx={{ color: BRAND.muted, fontSize: 11 }}>{sub}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* ══════════════════════════════════════════════
            SECTION 1 – KEY CONCEPTS
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<LayersIcon />} label="Key Concepts" />

                    <Grid container spacing={2}>
                        {[
                            {
                                icon: <ScheduleIcon />,
                                title: "The cron Daemon",
                                description:
                                    "crond is a background daemon that starts at boot and wakes up every minute. It scans all user crontabs and /etc/cron.* directories, then executes any job whose schedule matches the current time.",
                            },
                            {
                                icon: <ArticleIcon />,
                                title: "The Crontab File",
                                description:
                                    "Each user has their own crontab — a plain-text file listing scheduled jobs. The system also has its own crontab at /etc/crontab with an extra 'user' field. Edit with crontab -e, never edit files directly.",
                            },
                            {
                                icon: <AccessTimeIcon />,
                                title: "Cron Expression",
                                description:
                                    "A cron expression has 5 time fields (minute, hour, day-of-month, month, day-of-week) followed by the command. Wildcards (*), ranges (1-5), steps (*/15), and lists (1,3,5) give you fine-grained control.",
                            },
                            {
                                icon: <EmailIcon />,
                                title: "Cron Mail (MAILTO)",
                                description:
                                    "By default cron emails output to the owner's local mailbox. Set MAILTO=\"\" in your crontab to discard output, or MAILTO=\"you@example.com\" to receive it. Redirect output with >> /path/to/logfile 2>&1 to log instead.",
                            },
                            {
                                icon: <AutorenewIcon />,
                                title: "systemd Timers",
                                description:
                                    "Modern Linux systems also offer systemd timers as an alternative to cron. They integrate with journald for logging, support calendar expressions, and can be triggered on events — not just time. Both tools are widely used.",
                            },
                            {
                                icon: <AdminPanelSettingsIcon />,
                                title: "System vs User Cron",
                                description:
                                    "User crontabs (crontab -e) run as that user. System crontabs in /etc/crontab and /etc/cron.d/ include a 'username' field and run as any specified user, including root. Use system cron for privileged maintenance tasks.",
                            },
                        ].map((card) => (
                            <Grid item xs={12} sm={6} md={4} key={card.title}>
                                <ConceptCard {...card} />
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 2 – CRON SYNTAX DIAGRAM
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<CodeIcon />} label="Crontab Syntax" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        A cron job entry has <strong style={{ color: BRAND.white }}>5 time fields</strong> followed
                        by the command to run. Each colour below maps directly to a position in the expression.
                        Read it left to right: <em>when</em> exactly should this job fire?
                    </Typography>

                    {/* Visual colour-coded syntax diagram */}
                    <CronSyntaxDiagram />

                    {/* Special characters reference table */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.5 }}>
                        🟡 Special Characters
                    </Typography>

                    <TableContainer
                        component={Box}
                        sx={{
                            border: `1px solid ${BRAND.border}`,
                            borderRadius: 2,
                            overflowX: "auto",
                            mb: 3,
                        }}
                    >
                        <Table size="small" sx={{ minWidth: 400 }}>
                            <TableHead>
                                <TableRow sx={{ background: BRAND.goldGlow }}>
                                    {["Character", "Name", "Example", "Meaning"].map((h) => (
                                        <TableCell key={h} sx={tcStyle({ color: BRAND.gold, fontFamily: "monospace", fontSize: 11, letterSpacing: 1, textTransform: "uppercase", borderColor: BRAND.border })}>
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {[
                                    { char: "*",    name: "Wildcard", example: "* * * * *",      meaning: "Every possible value — matches any minute, hour, etc." },
                                    { char: ",",    name: "List",     example: "0,15,30,45",     meaning: "Multiple specific values — at 0, 15, 30, and 45 minutes" },
                                    { char: "-",    name: "Range",    example: "1-5",            meaning: "Inclusive range — Monday through Friday" },
                                    { char: "*/n",  name: "Step",     example: "*/15",           meaning: "Every nth value — every 15 minutes" },
                                    { char: "@",    name: "Shortcut", example: "@daily",         meaning: "Named schedule: @hourly, @daily, @weekly, @monthly, @reboot" },
                                ].map(({ char, name, example, meaning }) => (
                                    <TableRow
                                        key={char}
                                        sx={{ "&:hover": { background: "rgba(255,255,255,0.02)" }, transition: "background 0.2s" }}
                                    >
                                        <TableCell sx={tcStyle({ color: BRAND.goldBright, fontFamily: "monospace", fontWeight: 800, fontSize: 16, borderColor: BRAND.border })}>{char}</TableCell>
                                        <TableCell sx={tcStyle({ color: BRAND.white, fontWeight: 600, borderColor: BRAND.border })}>{name}</TableCell>
                                        <TableCell sx={tcStyle({ fontFamily: "monospace", color: BRAND.codeText, borderColor: BRAND.border })}>{example}</TableCell>
                                        <TableCell sx={tcStyle({ borderColor: BRAND.border })}>{meaning}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* @ shortcut reference */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.5 }}>
                        🟡 @ Schedule Shortcuts
                    </Typography>

                    <Box
                        sx={{
                            border: `1px solid ${BRAND.border}`,
                            borderRadius: 2,
                            overflow: "hidden",
                            mb: 2,
                        }}
                    >
                        {[
                            { shortcut: "@reboot",   equivalent: "At startup",           description: "Run once when the system boots — useful for startup scripts" },
                            { shortcut: "@hourly",   equivalent: "0 * * * *",            description: "At the start of every hour" },
                            { shortcut: "@daily",    equivalent: "0 0 * * *",            description: "At midnight every day (same as @midnight)" },
                            { shortcut: "@weekly",   equivalent: "0 0 * * 0",            description: "At midnight every Sunday" },
                            { shortcut: "@monthly",  equivalent: "0 0 1 * *",            description: "At midnight on the first of every month" },
                            { shortcut: "@yearly",   equivalent: "0 0 1 1 *",            description: "At midnight on 1st January every year (same as @annually)" },
                        ].map(({ shortcut, equivalent, description }) => (
                            <Box
                                key={shortcut}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    gap: { xs: 1, sm: 2 },
                                    px: 2,
                                    py: 1.3,
                                    borderBottom: `1px solid ${BRAND.border}`,
                                    "&:last-child": { borderBottom: "none" },
                                    "&:hover": { background: "rgba(255,255,255,0.02)" },
                                    transition: "background 0.2s",
                                }}
                            >
                                {/* Shortcut keyword */}
                                <Typography sx={{ fontFamily: "monospace", fontSize: 13, fontWeight: 700, color: BRAND.goldBright, minWidth: 100, flexShrink: 0 }}>
                                    {shortcut}
                                </Typography>

                                {/* Equivalent expression */}
                                <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: BRAND.muted, minWidth: { xs: "auto", sm: 110 }, flexShrink: 0 }}>
                                    {equivalent}
                                </Typography>

                                {/* Description */}
                                <Typography sx={{ fontSize: 13, color: BRAND.muted, flex: 1 }}>
                                    {description}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 3 – CRONTAB COMMANDS
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TerminalIcon />} label="Crontab Commands" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        The <code style={{ color: BRAND.goldBright }}>crontab</code> command manages your
                        scheduled jobs. You never edit the crontab file directly — always use{" "}
                        <code style={{ color: BRAND.goldBright }}>crontab -e</code> so the system can
                        validate syntax before saving.
                    </Typography>

                    {/* ── Core crontab commands ── */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>
                        🟡 Managing Your Crontab
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ crontab -e",           comment: "Open your crontab in the default editor (set EDITOR=nano if you prefer)" },
                            { cmd: "$ crontab -l",           comment: "List all your scheduled cron jobs" },
                            { cmd: "$ crontab -r",           comment: "Remove your entire crontab — use with caution, no confirmation!" },
                            { cmd: "$ crontab -i",           comment: "Interactive remove — prompts before deleting (safer than -r)" },
                            { cmd: "$ crontab -u alice -l",  comment: "List another user's crontab (requires root)" },
                            { cmd: "$ crontab -u alice -e",  comment: "Edit another user's crontab (requires root)" },
                        ]}
                    />

                    {/* Warning about -r */}
                    <WarningBox>
                        <code>crontab -r</code> deletes your entire crontab <strong>without asking for
                        confirmation</strong>. Always back up with <code>crontab -l {">"} ~/crontab.bak</code> before
                        making bulk changes, and prefer <code>crontab -i</code> for interactive removal.
                    </WarningBox>

                    {/* ── Checking cron status ── */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>
                        🟡 Checking cron Status & Logs
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ sudo systemctl status cron",          comment: "Check if the cron daemon is running (Ubuntu/Debian)" },
                            { cmd: "$ sudo systemctl status crond",         comment: "Same check on Fedora/CentOS/RHEL" },
                            { cmd: "$ sudo systemctl enable --now cron",    comment: "Enable and start cron if it's not running" },
                            { cmd: "$ grep CRON /var/log/syslog",           comment: "View cron execution log on Ubuntu/Debian" },
                            { cmd: "$ sudo journalctl -u cron",             comment: "View cron logs via systemd journal (newer distros)" },
                            { cmd: "$ sudo journalctl -u cron -f",          comment: "Follow cron logs in real time (-f = tail mode)" },
                            { cmd: "$ tail -f /var/log/cron",               comment: "Live cron log on Fedora/CentOS" },
                        ]}
                    />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 4 – CRON EXAMPLES TABLE
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<NotificationsActiveIcon />} label="Cron Expression Examples" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        The best way to learn cron is to read real examples. Each entry below shows the
                        expression, its human-readable meaning, and a category chip.
                    </Typography>

                    <Box
                        sx={{
                            border: `1px solid ${BRAND.border}`,
                            borderRadius: 2,
                            overflow: "hidden",
                            mb: 3,
                        }}
                    >
                        {/* Table column headers */}
                        <Box
                            sx={{
                                display: { xs: "none", sm: "flex" },
                                gap: 2,
                                px: 1.5,
                                py: 1,
                                background: BRAND.goldGlow,
                                borderBottom: `1px solid ${BRAND.border}`,
                            }}
                        >
                            {["Category", "Expression", "Meaning"].map((h) => (
                                <Typography key={h} sx={{ fontSize: 11, color: BRAND.gold, fontFamily: "monospace", letterSpacing: 1, textTransform: "uppercase" }}>
                                    {h}
                                </Typography>
                            ))}
                        </Box>

                        {/* Cron expression rows */}
                        {[
                            { expression: "* * * * *",           description: "Every single minute — useful for testing (disable after!)", category: "Every Min", color: BRAND.fieldMin   },
                            { expression: "*/5 * * * *",          description: "Every 5 minutes — for health checks or polling scripts",   category: "Minutes",   color: BRAND.fieldMin   },
                            { expression: "0 * * * *",            description: "At the top of every hour (minute 0)",                      category: "Hourly",    color: BRAND.fieldHour  },
                            { expression: "30 6 * * *",           description: "Every day at 6:30 AM",                                     category: "Daily",     color: BRAND.fieldDom   },
                            { expression: "0 0 * * *",            description: "Every day at midnight",                                    category: "Daily",     color: BRAND.fieldDom   },
                            { expression: "0 2 * * *",            description: "Every day at 2:00 AM — popular for nightly backups",       category: "Daily",     color: BRAND.fieldDom   },
                            { expression: "0 9 * * 1",            description: "Every Monday at 9:00 AM (1 = Monday)",                    category: "Weekly",    color: BRAND.fieldDow   },
                            { expression: "0 9 * * 1-5",          description: "Monday to Friday at 9:00 AM — weekdays only",             category: "Weekdays",  color: BRAND.fieldDow   },
                            { expression: "0 9,17 * * 1-5",       description: "9 AM and 5 PM on weekdays — twice-daily checks",          category: "Weekdays",  color: BRAND.fieldDow   },
                            { expression: "0 0 1 * *",            description: "First day of every month at midnight",                    category: "Monthly",   color: BRAND.fieldMon   },
                            { expression: "0 0 1 1 *",            description: "Once a year on 1st January at midnight",                  category: "Yearly",    color: BRAND.fieldMon   },
                            { expression: "0 0 1,15 * *",         description: "1st and 15th of every month — semi-monthly jobs",         category: "Monthly",   color: BRAND.fieldMon   },
                            { expression: "*/15 9-17 * * 1-5",   description: "Every 15 minutes between 9 AM and 5 PM on weekdays",     category: "Complex",   color: BRAND.gold        },
                            { expression: "@reboot",              description: "Once at system boot — start services, mount drives, etc.",category: "Boot",      color: BRAND.successText },
                            { expression: "@daily",               description: "Shortcut for 0 0 * * * — cleaner and more readable",     category: "Shortcut",  color: BRAND.fieldCmd    },
                        ].map((row) => (
                            <CronExampleRow key={row.expression} {...row} />
                        ))}
                    </Box>

                    {/* Full crontab file examples */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.5 }}>
                        🟡 Real-World Crontab Entries
                    </Typography>

                    {/* Annotated crontab file block */}
                    <Box
                        sx={{
                            background: BRAND.codeBg,
                            border: `1px solid ${BRAND.border}`,
                            borderRadius: 2,
                            p: 2.5,
                            mb: 2,
                            overflowX: "auto",
                        }}
                    >
                        {[
                            { line: "# Redirect all cron output to a log file, silence email",                        isComment: true  },
                            { line: 'MAILTO=""'                                                                                          },
                            { line: 'SHELL=/bin/bash'                                                                                    },
                            { line: 'PATH=/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin'                                  },
                            { line: ""                                                                                                    },
                            { line: "# Nightly database backup at 2 AM",                                              isComment: true  },
                            { line: "0 2 * * * /home/deploy/scripts/backup-db.sh >> /var/log/backup.log 2>&1"                           },
                            { line: ""                                                                                                    },
                            { line: "# Health check every 5 minutes",                                                 isComment: true  },
                            { line: "*/5 * * * * /usr/local/bin/healthcheck.sh >> /var/log/healthcheck.log 2>&1"                        },
                            { line: ""                                                                                                    },
                            { line: "# Clear temp files every Sunday at 3 AM",                                        isComment: true  },
                            { line: '0 3 * * 0 find /tmp -type f -mtime +7 -delete'                                                     },
                            { line: ""                                                                                                    },
                            { line: "# Restart app server daily at 4 AM (blue-green window)",                         isComment: true  },
                            { line: "0 4 * * * sudo systemctl restart myapp.service"                                                    },
                            { line: ""                                                                                                    },
                            { line: "# Generate weekly report every Friday at 5 PM",                                  isComment: true  },
                            { line: "0 17 * * 5 /home/deploy/scripts/weekly-report.sh"                                                  },
                            { line: ""                                                                                                    },
                            { line: "# Run on reboot — start custom background service",                              isComment: true  },
                            { line: "@reboot /home/deploy/scripts/start-worker.sh &"                                                    },
                        ].map((entry, i) => (
                            <Typography
                                key={i}
                                sx={{
                                    fontFamily: "monospace",
                                    fontSize: { xs: 11, md: 12 },
                                    color: entry.isComment ? "rgba(255,255,255,0.3)" : BRAND.codeText,
                                    lineHeight: 2,
                                    whiteSpace: "pre",
                                }}
                            >
                                {entry.line || "\u00A0"}
                            </Typography>
                        ))}
                    </Box>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 5 – SYSTEM-WIDE CRON
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<FolderOpenIcon />} label="System-Wide Cron" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        Beyond user crontabs, Linux has several system-level locations for scheduled tasks.
                        These are used by the OS and installed packages to run maintenance jobs as root —
                        without relying on any user's crontab.
                    </Typography>

                    {/* System cron files */}
                    {[
                        {
                            path: "/etc/crontab",
                            icon: <ArticleIcon />,
                            description: "The main system crontab. Unlike user crontabs, it has a 6th field: the username to run as. Only root should edit this file.",
                            example: "0 2 * * * root /usr/sbin/backup.sh",
                            exampleNote: "Format: min hour dom mon dow USERNAME command",
                        },
                        {
                            path: "/etc/cron.d/",
                            icon: <FolderOpenIcon />,
                            description: "Drop-in directory for package-managed cron jobs. Files here use the same format as /etc/crontab (with a username field). Packages install jobs here to avoid touching /etc/crontab.",
                            example: "# /etc/cron.d/certbot\n0 */12 * * * root certbot renew --quiet",
                            exampleNote: "certbot uses this to renew SSL certificates automatically",
                        },
                        {
                            path: "/etc/cron.hourly/",
                            icon: <AccessTimeIcon />,
                            description: "Drop executable scripts here. /etc/crontab runs all scripts in this directory at the start of every hour. No cron expression needed — just drop the script.",
                            example: "# Just drop a script file — no cron syntax required:\n/etc/cron.hourly/my-check.sh",
                            exampleNote: "Script must be executable: chmod +x my-check.sh",
                        },
                        {
                            path: "/etc/cron.daily/",
                            icon: <ScheduleIcon />,
                            description: "Scripts here run once daily (typically 6:25 AM via /etc/crontab or anacron). Used by packages like logrotate and updatedb.",
                            example: "ls /etc/cron.daily/\n# logrotate  apt-compat  updatedb",
                            exampleNote: "View what's scheduled to run daily on your system",
                        },
                        {
                            path: "/etc/cron.weekly/ & /etc/cron.monthly/",
                            icon: <AutorenewIcon />,
                            description: "Scripts run weekly or monthly. Timing is managed by anacron, which ensures jobs run even if the machine was off at the scheduled time.",
                            example: "# anacron ensures these run even after downtime\ncat /etc/anacrontab",
                            exampleNote: "anacron is the safety net for machines that aren't always on",
                        },
                    ].map(({ path, icon, description, example, exampleNote }) => (
                        <Box
                            key={path}
                            sx={{
                                mb: 2,
                                border: `1px solid ${BRAND.border}`,
                                borderRadius: 2,
                                overflow: "hidden",
                                transition: "border-color 0.25s",
                                "&:hover": { borderColor: BRAND.borderHover },
                            }}
                        >
                            {/* ── Header row: icon + path ── */}
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    px: 2,
                                    py: 1.5,
                                    background: BRAND.goldGlow,
                                    borderBottom: `1px solid ${BRAND.border}`,
                                }}
                            >
                                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 18, flexShrink: 0 } })}
                                <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 12, md: 13 }, color: BRAND.goldBright, fontWeight: 700 }}>
                                    {path}
                                </Typography>
                            </Box>

                            {/* ── Body: description + example ── */}
                            <Box sx={{ px: 2, py: 2 }}>
                                <Typography sx={{ color: BRAND.muted, fontSize: 13, lineHeight: 1.7, mb: 1.5 }}>
                                    {description}
                                </Typography>

                                {/* Inline code example */}
                                <Box
                                    sx={{
                                        background: "rgba(0,0,0,0.3)",
                                        border: `1px solid ${BRAND.border}`,
                                        borderRadius: 1.5,
                                        p: 1.5,
                                    }}
                                >
                                    {example.split("\n").map((line, i) => (
                                        <Typography
                                            key={i}
                                            sx={{
                                                fontFamily: "monospace",
                                                fontSize: 12,
                                                color: line.startsWith("#") ? "rgba(255,255,255,0.3)" : BRAND.codeText,
                                                lineHeight: 1.8,
                                                whiteSpace: "pre",
                                            }}
                                        >
                                            {line}
                                        </Typography>
                                    ))}
                                </Box>

                                {/* Example note in italic muted text */}
                                <Typography sx={{ color: "rgba(255,255,255,0.3)", fontSize: 11, fontStyle: "italic", mt: 0.8, fontFamily: "monospace" }}>
                                    ↑ {exampleNote}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 6 – ADVANCED TOPICS
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<BuildIcon />} label="Advanced Topics" />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 2 }}>
                        🔽 Advanced Examples (click to expand)
                    </Typography>

                    {/* Accordion 1: Output & logging */}
                    <ExpandableExample title="Handling output, logging & error handling" icon={<ArticleIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            By default, cron emails any output to the job owner. In production, always redirect
                            output to a log file. The pattern{" "}
                            <code style={{ color: BRAND.goldBright }}>{">> /log/file 2>&1"}</code> sends both
                            stdout and stderr to the same file.{" "}
                            <code style={{ color: BRAND.goldBright }}>2>&1</code> means "redirect stderr (fd 2)
                            to wherever stdout (fd 1) is going."
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: '# Silence all output (discard stdout and stderr)',                                            },
                                { cmd: '*/5 * * * * /usr/bin/check.sh > /dev/null 2>&1'                                              },
                                { cmd: '',                                                                                             },
                                { cmd: '# Append stdout only to a log file',                                                          },
                                { cmd: '0 2 * * * /usr/bin/backup.sh >> /var/log/backup.log'                                          },
                                { cmd: '',                                                                                             },
                                { cmd: '# Append both stdout AND stderr to the same log file',                                        },
                                { cmd: '0 2 * * * /usr/bin/backup.sh >> /var/log/backup.log 2>&1'                                     },
                                { cmd: '',                                                                                             },
                                { cmd: '# Timestamp each log entry using tee',                                                        },
                                { cmd: '0 3 * * * /usr/bin/cleanup.sh 2>&1 | while IFS= read -r line; do echo "$(date): $line"; done >> /var/log/cleanup.log' },
                            ]}
                        />
                        <SuccessBox>
                            Always add <code>>> /var/log/jobname.log 2>&1</code> to every production cron job.
                            Silent failures are the hardest bugs to diagnose. Set up log rotation with{" "}
                            <code>logrotate</code> so logs don't fill your disk.
                        </SuccessBox>
                    </ExpandableExample>

                    {/* Accordion 2: Environment variables */}
                    <ExpandableExample title="Environment variables in crontab" icon={<SettingsIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            Cron runs with a <strong style={{ color: BRAND.white }}>minimal environment</strong> —
                            much less than your interactive shell. Your{" "}
                            <code style={{ color: BRAND.goldBright }}>PATH</code>,{" "}
                            <code style={{ color: BRAND.goldBright }}>HOME</code>, and other variables may not
                            be set. This is the most common cause of cron jobs that "work in the terminal but
                            fail in cron." Always use absolute paths, or set variables at the top of your crontab.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: '# Set at the top of your crontab — applies to all jobs below'                              },
                                { cmd: 'SHELL=/bin/bash'                                                                            },
                                { cmd: 'PATH=/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin'                         },
                                { cmd: 'HOME=/home/deploy'                                                                          },
                                { cmd: 'MAILTO="ops@example.com"'                                                                   },
                                { cmd: ''                                                                                            },
                                { cmd: '# Use absolute paths in commands to be safe'                                                },
                                { cmd: '0 2 * * * /usr/bin/python3 /home/deploy/scripts/backup.py'                                 },
                                { cmd: ''                                                                                            },
                                { cmd: '# Debug cron environment: dump env to a file',                                              },
                                { cmd: '* * * * * env > /tmp/cron-env.txt 2>&1'                                                    },
                            ]}
                        />
                    </ExpandableExample>

                    {/* Accordion 3: Locking — prevent overlapping runs */}
                    <ExpandableExample title="Preventing overlapping cron runs with flock" icon={<StorageIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            If a cron job takes longer than its interval, two copies can run simultaneously —
                            causing race conditions, duplicate work, or data corruption.{" "}
                            <code style={{ color: BRAND.goldBright }}>flock</code> acquires a file lock before
                            running and exits immediately if another instance already holds the lock.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: '# flock syntax: flock -n /lockfile command',                                               },
                                { cmd: '*/5 * * * * flock -n /tmp/myjob.lock /home/deploy/scripts/process.sh >> /var/log/process.log 2>&1' },
                                { cmd: ''                                                                                           },
                                { cmd: '# Alternatively — check for PID file manually inside the script:',                        },
                                { cmd: '*/5 * * * * /home/deploy/scripts/safe-run.sh'                                             },
                            ]}
                        />
                        <Box
                            sx={{
                                background: BRAND.codeBg,
                                border: `1px solid ${BRAND.border}`,
                                borderRadius: 2,
                                p: 2,
                            }}
                        >
                            <Typography sx={{ fontFamily: "monospace", fontSize: 11, color: "rgba(255,255,255,0.3)", mb: 0.5 }}>
                                # safe-run.sh — basic PID-file locking pattern
                            </Typography>
                            {[
                                "#!/bin/bash",
                                "PIDFILE=/tmp/safe-run.pid",
                                "",
                                "if [ -f \"$PIDFILE\" ] && kill -0 $(cat \"$PIDFILE\") 2>/dev/null; then",
                                '  echo "Already running — skipping" && exit 1',
                                "fi",
                                "",
                                'echo $$ > "$PIDFILE"',
                                "trap 'rm -f \"$PIDFILE\"' EXIT",
                                "",
                                "# Your actual job here",
                                "/usr/bin/python3 /home/deploy/scripts/process.py",
                            ].map((line, i) => (
                                <Typography key={i} sx={{ fontFamily: "monospace", fontSize: 12, color: line.startsWith("#") ? "rgba(255,255,255,0.3)" : BRAND.codeText, lineHeight: 1.8 }}>
                                    {line || "\u00A0"}
                                </Typography>
                            ))}
                        </Box>
                    </ExpandableExample>

                    {/* Accordion 4: systemd timers */}
                    <ExpandableExample title="systemd timers — the modern alternative to cron" icon={<AutorenewIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            systemd timers are the modern alternative. Each timer requires two files: a{" "}
                            <code style={{ color: BRAND.goldBright }}>.service</code> (what to run) and a{" "}
                            <code style={{ color: BRAND.goldBright }}>.timer</code> (when to run it). Benefits over
                            cron: better logging via <code style={{ color: BRAND.goldBright }}>journalctl</code>,
                            dependency ordering, and event-based triggers (not just time-based).
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: "$ sudo systemctl list-timers --all",    comment: "List all active and inactive systemd timers" },
                                { cmd: "$ sudo systemctl status backup.timer",  comment: "Check the status of a specific timer" },
                                { cmd: "$ sudo systemctl enable backup.timer",  comment: "Enable a timer to start at boot" },
                                { cmd: "$ sudo systemctl start backup.timer",   comment: "Start the timer immediately" },
                                { cmd: "$ sudo journalctl -u backup.service",   comment: "View logs for the service the timer triggered" },
                            ]}
                        />
                        <Typography sx={{ color: BRAND.muted, fontSize: 12, mb: 1, lineHeight: 1.7 }}>
                            Example <code style={{ color: BRAND.goldBright }}>/etc/systemd/system/backup.timer</code>:
                        </Typography>
                        <Box
                            sx={{
                                background: BRAND.codeBg,
                                border: `1px solid ${BRAND.border}`,
                                borderRadius: 2,
                                p: 2,
                            }}
                        >
                            {[
                                "[Unit]",
                                "Description=Daily database backup",
                                "",
                                "[Timer]",
                                "OnCalendar=*-*-* 02:00:00",
                                "Persistent=true",
                                "",
                                "[Install]",
                                "WantedBy=timers.target",
                            ].map((line, i) => (
                                <Typography key={i} sx={{ fontFamily: "monospace", fontSize: 12, color: line.startsWith("[") ? BRAND.goldBright : BRAND.codeText, lineHeight: 1.8 }}>
                                    {line || "\u00A0"}
                                </Typography>
                            ))}
                        </Box>
                    </ExpandableExample>

                    {/* Accordion 5: Debugging cron */}
                    <ExpandableExample title="Debugging cron jobs that silently fail" icon={<BugReportIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            Cron failures are notoriously hard to debug because the environment is different
                            from your shell. Follow this systematic checklist when a cron job doesn't run.
                        </Typography>
                        {[
                            { step: "1", label: "Check cron is running",       cmd: "sudo systemctl status cron" },
                            { step: "2", label: "Check the cron log",          cmd: "grep CRON /var/log/syslog | tail -20" },
                            { step: "3", label: "Dump cron's environment",     cmd: "* * * * * env > /tmp/cronenv.txt 2>&1" },
                            { step: "4", label: "Test with absolute paths",    cmd: "/usr/bin/python3 /full/path/to/script.py" },
                            { step: "5", label: "Redirect output to log file", cmd: "*/1 * * * * /path/script.sh >> /tmp/test.log 2>&1" },
                            { step: "6", label: "Check file permissions",      cmd: "ls -la /path/to/script.sh  # must be executable" },
                        ].map(({ step, label, cmd }) => (
                            <Box key={step} sx={{ display: "flex", gap: 1.5, mb: 1.5, alignItems: "flex-start" }}>
                                {/* Step number badge */}
                                <Box
                                    sx={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: "50%",
                                        background: BRAND.goldGlow,
                                        border: `1px solid ${BRAND.border}`,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                        mt: 0.2,
                                    }}
                                >
                                    <Typography sx={{ fontFamily: "monospace", fontSize: 11, fontWeight: 700, color: BRAND.gold }}>{step}</Typography>
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ color: BRAND.white, fontSize: 13, fontWeight: 600, mb: 0.3 }}>{label}</Typography>
                                    <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: BRAND.codeText }}>{cmd}</Typography>
                                </Box>
                            </Box>
                        ))}
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 7 – WHAT YOU HAVE LEARNED
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="What You've Learned" />

                    <Grid container spacing={1.5}>
                        {[
                            "What cron is and how the crond daemon works",
                            "The 5-field cron expression syntax and all special characters",
                            "How to use @ schedule shortcuts like @daily and @reboot",
                            "How to create, list, and safely remove crontabs",
                            "How to check cron status and read cron logs",
                            "How to write real-world crontab entries with logging",
                            "The difference between user crontabs and system-wide cron",
                            "What /etc/cron.d/, /etc/cron.daily/, and anacron do",
                            "How to handle output, environment variables, and absolute paths",
                            "How to prevent overlapping cron runs with flock",
                            "How to use systemd timers as a modern cron alternative",
                            "A systematic debugging checklist for silent cron failures",
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1, flexShrink: 0 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13, lineHeight: 1.6 }}>
                                        {item}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 8 – RESOURCES
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 4 }}>
                    <SectionTitle icon={<LinkIcon />} label="Further Resources" />

                    <ResourceLink
                        href="https://crontab.guru/"
                        label="Crontab Guru — Interactive Expression Editor"
                        sub="Paste any cron expression and instantly see a human-readable translation. The go-to debugging tool."
                    />
                    <ResourceLink
                        href="https://linuxize.com/post/scheduling-cron-jobs-with-crontab/"
                        label="Scheduling Cron Jobs — Linuxize"
                        sub="Comprehensive guide covering crontab syntax, examples, and best practices."
                    />
                    <ResourceLink
                        href="https://man7.org/linux/man-pages/man5/crontab.5.html"
                        label="crontab(5) — Linux Man Page"
                        sub="Official man page: all fields, syntax rules, and environment variable behaviour."
                    />
                    <ResourceLink
                        href="https://www.digitalocean.com/community/tutorials/how-to-use-cron-to-automate-tasks-ubuntu-1804"
                        label="How to Use Cron to Automate Tasks — DigitalOcean"
                        sub="Practical tutorial including user crontabs, /etc/cron.d, and real automation examples."
                    />
                    <ResourceLink
                        href="https://www.freedesktop.org/software/systemd/man/systemd.timer.html"
                        label="systemd.timer — Official Documentation"
                        sub="Full reference for systemd timers including OnCalendar syntax and Persistent option."
                    />
                </Box>

                {/* ── Footer pro tip ── */}
                <Box
                    sx={{
                        mt: 2,
                        p: 2.5,
                        border: `1px solid ${BRAND.border}`,
                        borderRadius: 2,
                        background: BRAND.goldGlow,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                    }}
                >
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0, mt: 0.1 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13, lineHeight: 1.7 }}>
                        <strong style={{ color: BRAND.white }}>Pro tip for juniors:</strong> Before deploying
                        any cron job, test your command manually in the terminal first. Then add{" "}
                        <code style={{ color: BRAND.goldBright }}>>> /tmp/test.log 2>&1</code> and set the
                        interval to{" "}
                        <code style={{ color: BRAND.goldBright }}>* * * * *</code> (every minute) to verify
                        it fires correctly. Once confirmed, change to the real interval and remove the test
                        logging. Use{" "}
                        <code style={{ color: BRAND.goldBright }}>crontab.guru</code> to validate your
                        expression before saving.
                    </Typography>
                </Box>

            </Container>
        </Box>
    );
}
